import axios from 'axios';
import {skinCorrente} from '../skin';
import i18next from 'i18next';
import {nellaLingua} from './erroriConto';

/**
 * TELEGRAM sul sito, dopo la registrazione: entrare e collegarlo al conto.
 *
 * COSA FA: chiede al servizio un nonce (usa e getta), apre la finestra di
 * Telegram con quel nonce e rimanda al servizio la conferma. Il gettone di
 * accesso che torna e' SEMPRE quello della piattaforma: Telegram qui dice solo
 * chi sei, non entra nei servizi.
 * COSA NON FA: non salva niente da solo (lo fa UserService, come per la
 * password); non decide se un conto puo' entrare (lo decide il servizio).
 */
const LIBRERIA = 'https://telegram.org/js/telegram-login.js';

function env() {
  return (typeof window !== 'undefined' && window.env) || {};
}

function base() {
  const e = env();
  return `${e.REACT_APP_API_URL || ''}/${e.REACT_APP_API_VERSION || 'v1'}/users-management-service`;
}

function intestazioni(gettone) {
  const h = {'Content-Type': 'application/json', 'X-Client-Surface': 'FRONTEND'};
  h.TENANT_ID = env().REACT_APP_API_TENANT || '1';
  if (gettone) h.Authorization = `Bearer ${gettone}`;
  return h;
}

/** Il Client ID del bot della skin; vuoto = Telegram spento per questa skin. */
export function clientIdTelegram() {
  const s = skinCorrente() || {};
  return (s.registrazione && s.registrazione.telegramClientId) || '';
}

/** Il nome del bot della skin, senza @ (Skin › Registrazione › Telegram); vuoto = nessun bot. */
export function botTelegram() {
  const s = skinCorrente() || {};
  const c = (s.registrazione && s.registrazione.canali) || {};
  return String(c.telegram || '').trim().replace(/^@/, '');
}

/** Si sta usando un telefono o un tablet: li' Telegram e' un'app, non una pagina. */
export function daTelefono() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
    || (navigator.maxTouchPoints > 1 && window.innerWidth < 900);
}

/**
 * CON IL BOT, OVUNQUE. Il «Log in with Telegram» dal telefono finisce nell'app di
 * Telegram e il ritorno al sito non e' affidabile (iPhone): si apre invece il
 * bot della skin (t.me/<bot>?start=…), che chiede il numero e rimanda al sito
 * con un pulsante e un biglietto. Lo stesso giro vale anche dal computer (23
 * ottobre 2026): un solo modo, uguale dappertutto; senza bot in Skin ›
 * Registrazione resta la finestra o il redirect di Telegram.
 */
export function viaBotTelegram() {
  return !!botTelegram() && !!clientIdTelegram();
}

/**
 * La conferma arriva da un'altra parte (il bot dal telefono, o Telegram che
 * riporta al sito col redirect): niente finestra, il browser va via e torna.
 */
export function ritornoAutomaticoTelegram() {
  if (viaBotTelegram()) return true;
  const s = skinCorrente() || {};
  return !!(s.registrazione && s.registrazione.telegramRedirect);
}

/** Dal telefono: chiede il biglietto e apre il bot. Non ritorna: la pagina cambia. */
async function avviaBotTelegram(scopo, dopo) {
  const r = await axios.post(`${base()}/api/login/telegram/biglietto`,
      {scopo, dominio: dominioCorrente(), dopo: dopo || null}, {headers: intestazioni(), validateStatus: () => true});
  if (r.status !== 200 || !r.data || !r.data.link) {
    throw new Error(nellaLingua(r.data && r.data.message) || i18next.t('Servizio non raggiungibile: riprova fra poco'));
  }
  window.location.assign(r.data.link);
  return new Promise(() => {});
}

/** Tornati dal bot col biglietto nell'indirizzo: si riscatta la conferma. */
async function riscattaBiglietto(biglietto) {
  const r = await axios.post(`${base()}/api/login/telegram/biglietto/riscatta`,
      {biglietto, dominio: dominioCorrente()}, {headers: intestazioni(), validateStatus: () => true});
  return {status: r.status, d: r.data || {}};
}

/* ------------------------------------------------------------------ *
 * IL RITORNO AUTOMATICO.
 *
 * Con la finestra, dal telefono si finisce nell'app di Telegram e il browser
 * resta dietro: la conferma arriva ma nessuno la vede. Con il redirect il
 * browser VA sulla pagina di Telegram e Telegram lo RIPORTA sulla nostra
 * pagina con un codice nell'indirizzo: non c'e' niente da riaprire.
 *
 * Il giro: avviaRitornoTelegram(scopo) chiede al servizio un AVVIO (nonce,
 * PKCE, state) dicendogli cosa si sta facendo (registrazione / accesso /
 * collega) e dove tornare, e manda il browser da Telegram. Tornati, la pagina
 * chiama ritornoTelegram(): manda SOLO codice e state al servizio, che ritrova
 * la richiesta, scambia il codice con Telegram (serve il Client Secret, che sta
 * solo nel servizio) e torna l'id_token piu' cosa si stava facendo: da li'
 * tutto prosegue come con la finestra. Il nonce lo consuma il passo che segue.
 *
 * PERCHE' NIENTE NEL BROWSER: dal telefono Telegram riapre il sito in una
 * scheda NUOVA (o nel suo browser interno), dove la memoria della scheda di
 * partenza (sessionStorage) non c'e'. Con la richiesta nel servizio il ritorno
 * funziona da qualunque scheda o browser.
 * ------------------------------------------------------------------ */

/* DOVE TELEGRAM DEVE TORNARE, per ogni scopo: sono gli indirizzi registrati in
   BotFather (Redirect URI), e NON la pagina corrente. Registrati e Accedi sono
   finestre sopra la pagina: chi clicca puo' stare sulla home o su una partita,
   e Telegram rifiuta un indirizzo non registrato. Al ritorno, /register e /login
   riaprono la loro finestra (RegistrazioneDaLink, AccessoDaLink). */
const RITORNI = {
  registrazione: '/register',
  accesso: '/login',
  collega: '/my-account/sicurezza',
};

/** L'indirizzo di ritorno per questo scopo, senza parametri: e' il redirect_uri registrato in BotFather. */
function indirizzoDiRitorno(scopo) {
  return window.location.origin + (RITORNI[scopo] || window.location.pathname);
}

/** Cosa si stava facendo, dedotto dalla pagina su cui Telegram e' tornato (se il servizio non lo sa piu'). */
function scopoDallaPagina() {
  const p = (typeof window !== 'undefined' && window.location.pathname) || '';
  if (p.startsWith('/register')) return 'registrazione';
  if (p.startsWith('/login')) return 'accesso';
  if (p.indexOf('/sicurezza') >= 0) return 'collega';
  return null;
}

/**
 * Manda il browser da Telegram. Non ritorna: la pagina cambia. `scopo` e'
 * cio' che la pagina dovra' riprendere al ritorno: 'registrazione', 'accesso',
 * 'collega'. `dopo` (facoltativo) e' dove andare a cose fatte.
 */
export async function avviaRitornoTelegram(scopo, dopo) {
  const clientId = clientIdTelegram();
  if (!clientId) throw new Error(i18next.t('Telegram non e\' attivo per questo sito'));
  if (viaBotTelegram()) return avviaBotTelegram(scopo, dopo);
  const redirectUri = indirizzoDiRitorno(scopo);
  const r = await axios.post(`${base()}/api/login/telegram/avvio`,
      {scopo, redirectUri, dopo: dopo || null}, {headers: intestazioni(), validateStatus: () => true});
  if (r.status !== 200 || !r.data || !r.data.state) {
    throw new Error(nellaLingua(r.data && r.data.message) || i18next.t('Servizio non raggiungibile: riprova fra poco'));
  }
  const p = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: 'openid profile phone',
    state: r.data.state,
    nonce: r.data.nonce,
    code_challenge: r.data.codeChallenge,
    code_challenge_method: 'S256',
    lang: 'it',
  });
  window.location.assign('https://oauth.telegram.org/auth?' + p.toString());
  // il browser sta andando via: chi aspetta non deve fare altro
  return new Promise(() => {});
}

/**
 * Da chiamare quando la pagina si apre: se nell'indirizzo c'e' il codice del
 * ritorno, lo manda al servizio con lo state e torna {scopo, idToken, nonce,
 * user, dopo}. Altrimenti null. Pulisce l'indirizzo.
 * Un errore di Telegram (annullato, scaduto) torna {scopo, errore}.
 */
export async function ritornoTelegram(ricerca, scopoAtteso) {
  if (typeof window === 'undefined') return null;
  // `ricerca` (facoltativa): la query di quando si e' tornati, se chi chiama ha
  // gia' cambiato indirizzo (la finestra di registrazione aperta da /register).
  const dallaFinestra = ricerca === undefined || ricerca === null;
  const q = new URLSearchParams(dallaFinestra ? window.location.search : ricerca);
  const code = q.get('code');
  const state = q.get('state');
  const errore = q.get('error');
  const biglietto = q.get('biglietto');
  if (!code && !errore && !biglietto) return null;
  // l'indirizzo torna pulito: un F5 non deve rimandare lo stesso codice
  if (dallaFinestra) {
    try { window.history.replaceState(window.history.state, '', window.location.pathname); } catch (e) { /* niente */ }
  }
  // DAL BOT (telefono): il biglietto si riscatta; da Telegram (redirect): il codice si scambia.
  // Le due risposte hanno la stessa forma: idToken (o «conferma:…»), nonce, scopo, dopo, user.
  let r;
  if (biglietto) {
    r = await riscattaBiglietto(biglietto);
  } else {
    const x = await axios.post(`${base()}/api/login/telegram/scambio`,
        {code, state, error: errore, errorDescription: q.get('error_description'), dominio: dominioCorrente()},
        {headers: intestazioni(), validateStatus: () => true});
    r = {status: x.status, d: x.data || {}};
  }
  const d = r.d;
  // se il servizio non sa piu' lo scopo (richiesta scaduta), vale quello di chi chiama,
  // poi la pagina: la finestra di registrazione sta sulla home, che non dice niente
  const scopo = d.scopo || scopoAtteso || scopoDallaPagina();
  const dopo = d.dopo || null;
  if (r.status !== 200 || !d.idToken) {
    const annullato = d.code === 'TELEGRAM_ANNULLATO' || (!d.code && errore === 'access_denied');
    return {scopo, dopo, errore: annullato ? 'annullato' : (d.message || i18next.t('Errore {{stato}}', {stato: r.status}))};
  }
  return {scopo, dopo, idToken: d.idToken, nonce: d.nonce, user: d.user || {}};
}

/** Collega al conto con una conferma gia' in mano (dal ritorno automatico). */
export async function collegaTelegramCon(gettone, idToken, nonce) {
  const r = await axios.post(`${base()}/api/users/telegram/collega`,
      {idToken, nonce, dominio: dominioCorrente()}, {headers: intestazioni(gettone), validateStatus: () => true});
  if (r.status < 200 || r.status >= 300) throw new Error(nellaLingua(r.data && r.data.message) || i18next.t('Errore {{stato}}', {stato: r.status}));
  return r.data;
}

let caricamento = null;

/** Carica la libreria di Telegram una volta sola (da chiamare quando la pagina si apre, non al clic). */
export function caricaLibreriaTelegram() {
  if (typeof window === 'undefined') return Promise.reject(new Error('senza finestra'));
  if (window.Telegram && window.Telegram.Login) return Promise.resolve();
  if (caricamento) return caricamento;
  caricamento = new Promise((ok, no) => {
    const sc = document.createElement('script');
    sc.src = LIBRERIA;
    sc.async = true;
    sc.onload = () => ok();
    sc.onerror = () => { caricamento = null; no(new Error(i18next.t('Non riesco a caricare Telegram: controlla la connessione'))); };
    document.head.appendChild(sc);
  });
  return caricamento;
}

/** Il nonce del server. Si chiede PRIMA di aprire la finestra. */
async function nuovoNonce() {
  const r = await axios.get(`${base()}/api/login/telegram/nonce`, {headers: intestazioni(), validateStatus: () => true});
  if (r.status !== 200 || !r.data || !r.data.nonce) {
    throw new Error(nellaLingua(r.data && r.data.message) || i18next.t('Servizio non raggiungibile: riprova fra poco'));
  }
  return r.data.nonce;
}

/**
 * Apre la finestra di Telegram e torna con la conferma. Il nonce va chiesto
 * prima e passato qui, cosi' la finestra si apre nel gesto del clic.
 * Lancia Error con .annullato = true se la persona ha chiuso la finestra.
 */
function finestra(clientId, nonce) {
  return new Promise((ok, no) => {
    if (!window.Telegram || !window.Telegram.Login) {
      no(new Error(i18next.t('Telegram si sta caricando: riprova fra un attimo')));
      return;
    }
    window.Telegram.Login.auth(
      // anche il telefono: se poi serve la registrazione, il numero e' gia' verificato
      {client_id: Number(clientId), scope: ['profile', 'phone'], nonce, lang: 'it'},
      (esito) => {
        if (!esito || esito.error || !esito.id_token) {
          const e = new Error(i18next.t('Telegram non ha confermato ({{v0}})', {v0: (esito && esito.error) || 'nessuna risposta'}));
          e.annullato = !!(esito && esito.error === 'popup_closed');
          no(e);
          return;
        }
        ok({idToken: esito.id_token, user: esito.user || {}});
      });
  });
}

/* Il nonce gia' chiesto, per aprire la finestra SENZA aspettare la rete: dopo
   una chiamata asincrona il browser (Safari in testa) non considera piu' la
   finestra frutto di un clic e la blocca. Vale cinque minuti, una volta sola. */
let pronto = null;
const VALE_MS = 4 * 60 * 1000;

/** Da chiamare quando la pagina si apre (e dopo ogni uso): carica la libreria e chiede il nonce. */
export async function preparaTelegram() {
  if (!clientIdTelegram()) return;
  await caricaLibreriaTelegram();
  const nonce = await nuovoNonce();
  pronto = {nonce, scade: Date.now() + VALE_MS};
}

/**
 * Tiene pronto il nonce finche' la pagina e' aperta: lo prepara subito e lo
 * rinnova ogni tre minuti (vale cinque), cosi' il clic trova sempre la
 * finestra pronta anche dopo una pausa. Ritorna la funzione per smettere.
 */
export function mantieProntoTelegram() {
  if (!clientIdTelegram()) return () => {};
  preparaTelegram().catch(() => { /* si riprova al clic */ });
  const t = setInterval(() => { preparaTelegram().catch(() => { /* idem */ }); }, 3 * 60 * 1000);
  return () => clearInterval(t);
}

/**
 * Il giro completo di un'azione con Telegram: finestra → conferma.
 * Ritorna {idToken, nonce, user} (user = chi e', come lo dice Telegram: id,
 * name, preferred_username, phone_number). Va chiamata DENTRO il clic, senza
 * aspettare niente prima: se il nonce e' pronto la finestra si apre subito.
 */
export async function confermaConTelegram() {
  const clientId = clientIdTelegram();
  if (!clientId) throw new Error(i18next.t('Telegram non e\' attivo per questo sito'));
  let nonce = null;
  if (pronto && pronto.scade > Date.now()) {
    nonce = pronto.nonce;
  }
  pronto = null; // usa e getta, anche se poi la finestra non si apre
  if (!nonce) {
    // non era pronto: si prepara adesso. La finestra potrebbe essere bloccata dal browser: il clic dopo funziona.
    await caricaLibreriaTelegram();
    nonce = await nuovoNonce();
  }
  const {idToken, user} = await finestra(clientId, nonce);
  return {idToken, nonce, user};
}

export function dominioCorrente() {
  return typeof window !== 'undefined' ? window.location.hostname : '';
}

/** Lo stato del collegamento del proprio conto: {collegato, username}. */
export async function statoTelegram(gettone) {
  const r = await axios.get(`${base()}/api/users/telegram`, {headers: intestazioni(gettone), validateStatus: () => true});
  if (r.status !== 200) throw new Error(nellaLingua(r.data && r.data.message) || i18next.t('Errore {{stato}}', {stato: r.status}));
  return r.data || {collegato: false};
}

/** Collega Telegram al conto di chi e' dentro. Lancia Error col messaggio del servizio. */
export async function collegaTelegram(gettone) {
  const {idToken, nonce} = await confermaConTelegram();
  const r = await axios.post(`${base()}/api/users/telegram/collega`,
      {idToken, nonce, dominio: dominioCorrente()}, {headers: intestazioni(gettone), validateStatus: () => true});
  if (r.status < 200 || r.status >= 300) throw new Error(nellaLingua(r.data && r.data.message) || i18next.t('Errore {{stato}}', {stato: r.status}));
  return r.data;
}

export async function scollegaTelegram(gettone) {
  const r = await axios.delete(`${base()}/api/users/telegram`, {headers: intestazioni(gettone), validateStatus: () => true});
  if (r.status < 200 || r.status >= 300) throw new Error(nellaLingua(r.data && r.data.message) || i18next.t('Errore {{stato}}', {stato: r.status}));
  return r.data;
}

/* ------------------------------------------------------------------ *
 * IL BROWSER INTERNO DI TELEGRAM. Dal telefono il pulsante del bot apre il
 * sito dentro Telegram, che ha una memoria sua: l'accesso fatto li' non vale
 * in Safari o Chrome. Il sito se ne accorge e, prima di usare il biglietto,
 * propone «Apri nel browser»: il biglietto passa intatto al browser vero.
 * ------------------------------------------------------------------ */

/** Il sito e' aperto nel browser interno di Telegram (gli oggetti che Telegram mette nella pagina, o il nome nello user agent). */
export function dentroTelegram() {
  if (typeof window === 'undefined') return false;
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  return !!(window.TelegramWebviewProxy || window.TelegramWebviewProxyProto || window.TelegramWebview) || /Telegram/i.test(ua);
}

/** Lo stesso indirizzo, da aprire FUORI da Telegram: Safari su iPhone, Chrome su Android. Null se non si sa come. */
export function indirizzoNelBrowser(url) {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  const senzaSchema = String(url).replace(/^https?:\/\//, '');
  if (/iPhone|iPad|iPod/i.test(ua)) return `x-safari-https://${senzaSchema}`;
  if (/Android/i.test(ua)) {
    return `intent://${senzaSchema}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
  }
  return null;
}
